import { parseFiniteNumber } from "@/domain/quant/number-parsing";
import { classColor } from "@/domain/clients/class-palette";
import { roleColor } from "@/domain/clients/role-allocation";
import type { RoleAllocationSlice } from "@/domain/clients/types";

const ROLE_ASSET_CLASS_ORDER_ID = "1";

const CLASS_ASSET_CLASS_ORDER_ID = "2";

const DISTRIBUTION_SCALE_VALUE = 0;

const DISTRIBUTION_SCALE_WEIGHT = 4;

export function assetClassDistributionQuery(): string {
  return `?scale_value=${DISTRIBUTION_SCALE_VALUE}` + `&scale_weight=${DISTRIBUTION_SCALE_WEIGHT}`;
}

interface AssetClassWire {
  id?: string | null;
  name?: string | null;
  quant_asset_class_order_id?: string | null;
  virtual_for_quant_asset_class_id?: string | null;
  liquidity?: boolean | null;
}

interface AssetClassOrderWire {
  id?: string | null;
  name?: string | null;
}

interface AssetClassDistributionGroupWire {
  asset_class?: AssetClassWire | null;
  value?: string | number | null;
  weight?: string | number | null;
}

interface AssetClassDistributionOrderWire {
  asset_class_order?: AssetClassOrderWire | null;
  groups?: AssetClassDistributionGroupWire[] | null;
}

export interface AssetClassDistributionWire {
  total_value?: string | number | null;
  orders?: AssetClassDistributionOrderWire[] | null;
}

export function toDistributionLevels(wire: AssetClassDistributionWire): DistributionLevels | null {
  const role = toOrderDistribution(wire, ROLE_ASSET_CLASS_ORDER_ID, roleColor);
  if (role === null) return null;
  return {
    role,
    class: toOrderDistribution(wire, CLASS_ASSET_CLASS_ORDER_ID, classColor),
  };
}

export interface DistributionLevels {
  role: RoleAllocationSlice[];
  class: RoleAllocationSlice[] | null;
}

function toOrderDistribution(
  wire: AssetClassDistributionWire,
  orderId: string,
  colorFor: (label: string | null) => string,
): RoleAllocationSlice[] | null {
  const orders = Array.isArray(wire.orders) ? wire.orders : [];
  const block = orders.find((o) => String(o?.asset_class_order?.id ?? "") === orderId);
  const groups = Array.isArray(block?.groups) ? block.groups : null;
  if (!groups || groups.length === 0) return null;

  const slices: RoleAllocationSlice[] = [];
  let total = 0;
  for (const g of groups) {
    const value = parseFiniteNumber(g?.value);
    const weight = parseFiniteNumber(g?.weight);
    if (value === null || weight === null) return null;
    const label = (g?.asset_class?.name ?? "").trim() || null;
    total += value;
    slices.push({
      label,
      color: colorFor(label),
      value,
      pct: weight * 100,
    });
  }
  if (total <= 0) return null;

  return slices.filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
}
